import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Container,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { getBaseTerms, updateTerms } from '../api/terms';
import './TermsEdit.css';

const BaseTermsEdit = () => {
  const navigate = useNavigate();
  const [termsContent, setTermsContent] = useState('');
  const [couponTermsContent, setCouponTermsContent] = useState('');
  const [originalTermsContent, setOriginalTermsContent] = useState('');
  const [originalCouponTermsContent, setOriginalCouponTermsContent] =
    useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const base = await getBaseTerms();
        if (!base.success) {
          throw new Error(base.message || 'ベース規約の読み込みに失敗しました');
        }

        const { general, coupon } = base;

        setTermsContent(general);
        setCouponTermsContent(coupon);
        setOriginalTermsContent(general);
        setOriginalCouponTermsContent(coupon);
      } catch (error) {
        console.error(error);
        setAlert({
          open: true,
          message: error.message || 'ベース規約の読み込みに失敗しました',
          severity: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    if (termsContent.trim() === '' || couponTermsContent.trim() === '') {
      setAlert({
        open: true,
        message: '利用規約とクーポン利用規約の両方の内容を入力してください',
        severity: 'warning',
      });
      return;
    }
    setIsSaving(true);
    try {
      const res = await updateTerms({
        general_content: termsContent,
        coupon_content: couponTermsContent,
      });
      setOriginalTermsContent(termsContent);
      setOriginalCouponTermsContent(couponTermsContent);
      setAlert({
        open: true,
        message: res.message || 'ベース規約を保存しました',
        severity: 'success',
      });
    } catch (error) {
      setAlert({
        open: true,
        message: error.message || '保存に失敗しました',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseAlert = () => setAlert({ ...alert, open: false });

  const hasChanges = () =>
    termsContent !== originalTermsContent ||
    couponTermsContent !== originalCouponTermsContent;

  if (isLoading) {
    return (
      <Container className="terms-edit-container">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container className="terms-edit-container" maxWidth="lg">
      <Paper className="terms-edit-paper" elevation={3}>
        <Box className="terms-edit-header">
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ backgroundColor: 'transparent' }}
          >
            ベース規約編集
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/terms-management')}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            規約管理に戻る
          </Button>
        </Box>

        {hasChanges() && (
          <Alert severity="info" sx={{ mb: 2 }}>
            未保存の変更があります
          </Alert>
        )}

        <Box className="terms-editor">
          <Box className="terms-section">
            <Typography variant="h6" gutterBottom>
              利用規約内容
            </Typography>
            <TextField
              multiline
              fullWidth
              rows={5}
              value={termsContent}
              onChange={(e) => setTermsContent(e.target.value)}
              placeholder="利用規約の内容を入力してください..."
              variant="outlined"
              className="terms-textfield"
            />
          </Box>

          <Box className="coupon-terms-section">
            <Typography variant="h6" gutterBottom>
              クーポン利用規約内容
            </Typography>
            <TextField
              multiline
              fullWidth
              rows={5}
              value={couponTermsContent}
              onChange={(e) => setCouponTermsContent(e.target.value)}
              placeholder="クーポン利用規約の内容を入力してください..."
              variant="outlined"
              className="terms-textfield coupon-terms"
            />
          </Box>
        </Box>

        <Box className="save-button-container">
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !hasChanges()}
            className="save-button green"
            size="large"
            sx={{
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#45a049' },
              '&:disabled': { backgroundColor: '#cccccc' },
              minWidth: '200px',
              padding: '12px 24px',
            }}
          >
            {isSaving ? '保存中...' : '保存'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BaseTermsEdit;
